/**
 * Manual correction step.
 *
 * Runs between vision output and scoring. Nothing reaches the scorer until a
 * human has confirmed it — this is the safety net that makes photo-based
 * input acceptable in a venue where the payouts are real.
 */

import { getRuleset } from '../rules';
import { isTile, rankOf, suitOf, validateTilePool } from '../rules/tiles';
import type { BonusTile, Suit, Tile } from '../rules/tiles';
import type { HandInput, Meld } from '../rules/types';
import { LOW_CONFIDENCE_THRESHOLD, type RecognisedHand } from './client';

export interface CorrectionDraft {
  tiles: Tile[];
  melds: Meld[];
  flowers: BonusTile[];
  /** Indices into `tiles` the operator should look at first. */
  needsReview: number[];
  /** Blocking problems. Non-empty disables the "confirm" button. */
  problems: string[];
}

/** Turn a recognition result into an editable draft. */
export function toDraft(hand: RecognisedHand): CorrectionDraft {
  const tiles = hand.tiles.map((t) => t.tile);
  const needsReview = hand.tiles
    .map((t, i) => (t.confidence < LOW_CONFIDENCE_THRESHOLD ? i : -1))
    .filter((i) => i >= 0);

  const melds: Meld[] = hand.suspectedKongs.map((k) => ({
    type: 'kong',
    tiles: k,
    concealed: false,
  }));

  return {
    tiles,
    melds,
    flowers: hand.flowers,
    needsReview,
    problems: checkDraft(tiles, melds),
  };
}

/** Re-validate after every operator edit. */
export function checkDraft(tiles: Tile[], melds: Meld[]): string[] {
  const all = [...tiles, ...melds.flatMap((m) => m.tiles)];
  const problems = validateTilePool(all);

  const kongs = melds.filter((m) => m.type === 'kong').length;
  const expected = 14 + kongs;
  if (all.length !== expected) {
    problems.push(`${all.length} tiles recognised; a winning hand needs ${expected}`);
  }
  return problems;
}

export interface CorrectionSuggestion {
  /** Index into `draft.tiles`. */
  index: number;
  current: Tile;
  suggested: Tile;
}

/**
 * For each low-confidence tile, tries every same-suit rank and keeps only
 * substitutions that make the WHOLE hand a valid completable shape (see
 * `Ruleset.isCompleteHand`) — a wrong reading elsewhere in the hand often
 * makes every substitution fail, which is fine: no suggestion beats a bad
 * one. Among survivors, prefers whichever is closest in rank to its
 * nearest same-suit neighbours in the tile sequence, since tiles are
 * typically laid out in ascending runs, so the correct rank usually
 * continues smoothly from what's already next to it rather than jumping.
 *
 * Never touches high-confidence tiles and never auto-applies anything —
 * this only proposes one best guess per flagged tile for a human to accept
 * or ignore in the correction screen.
 */
export function suggestCorrections(draft: CorrectionDraft): CorrectionSuggestion[] {
  if (draft.needsReview.length === 0) return [];

  const ruleset = getRuleset('hkos'); // the only ruleset today; pass one in if that changes

  const isComplete = (tiles: Tile[]) =>
    ruleset.isCompleteHand({
      concealed: tiles,
      melds: draft.melds,
      winningTile: tiles[0]!, // isCompleteHand never reads this field
    });

  // If the hand already reads as a valid shape, there's nothing to fix —
  // low confidence means uncertain, not necessarily wrong.
  if (isComplete(draft.tiles)) return [];

  const suggestions: CorrectionSuggestion[] = [];
  for (const i of draft.needsReview) {
    const current = draft.tiles[i];
    if (!current || !isTile(current)) continue;
    const suit = suitOf(current);
    if (suit === 'z') continue; // rank confusion hasn't shown up on honours; skip for now

    const before = nearestSameSuitRank(draft.tiles, i, suit, -1);
    const after = nearestSameSuitRank(draft.tiles, i, suit, 1);

    let best: { tile: Tile; distance: number } | undefined;
    for (let rank = 1; rank <= 9; rank++) {
      const candidate = `${rank}${suit}`;
      if (candidate === current) continue;
      const trial = [...draft.tiles];
      trial[i] = candidate;
      if (!isComplete(trial)) continue;
      const distances = [before, after]
        .filter((r): r is number => r !== undefined)
        .map((r) => Math.abs(rank - r));
      const distance = distances.length > 0 ? Math.min(...distances) : 0;
      if (!best || distance < best.distance) {
        best = { tile: candidate, distance };
      }
    }
    if (best) {
      suggestions.push({ index: i, current, suggested: best.tile });
    }
  }
  return suggestions;
}

/** Nearest tile of the same suit scanning outward from `from` in one direction. */
function nearestSameSuitRank(
  tiles: Tile[],
  from: number,
  suit: Suit,
  dir: 1 | -1,
): number | undefined {
  for (let i = from + dir; i >= 0 && i < tiles.length; i += dir) {
    const t = tiles[i];
    if (t !== undefined && isTile(t) && suitOf(t) === suit) return rankOf(t);
  }
  return undefined;
}

/** Operator confirms the draft; hand it to the scorer. */
export function confirmDraft(draft: CorrectionDraft, winningTile: Tile): HandInput {
  const problems = checkDraft(draft.tiles, draft.melds);
  if (problems.length > 0) {
    throw new Error(`Cannot confirm hand: ${problems.join('; ')}`);
  }
  return {
    concealed: draft.tiles,
    melds: draft.melds,
    winningTile,
    flowers: draft.flowers,
  };
}
